import { hasLLMSupport as baseHasLLMSupport } from './writing';
import { callOpenRouter } from './openrouter';
import { LLMOrchestrator } from './orchestrator';
import { buildInvoker } from './providerFactory';
import { readLLMTimeouts, getLLMLogger } from './config';
import type { LlmScore } from '../../listening/types';

type ListeningLLMInput = {
  originalSentence: string;
  userAnswer: string;
  diffSummary: string;
};

export type ListeningLLMFeedback = {
  errorTags: string[];
  suggestions: string[];
  provider?: string;
  latencyMs?: number;
};

// 静默分档输入类型
type ListeningScoreInput = {
  originalSentence: string;
  userAnswer: string;
  accuracy: number;
  mode: 'sentence' | 'cloze';
};

const readLocalFlag = () => {
  if (typeof localStorage === 'undefined') return undefined;
  return localStorage.getItem('enpass_llm_enabled') ?? undefined;
};

export async function hasListeningLLMSupport(): Promise<boolean> {
  const flag = readLocalFlag();
  if (flag !== 'true') return false;
  return baseHasLLMSupport();
}

const craftFallbackFeedback = (input: ListeningLLMInput): ListeningLLMFeedback => {
  const tags: string[] = [];
  if (/missing:/i.test(input.diffSummary)) tags.push('漏听/漏写');
  if (/subs:/i.test(input.diffSummary)) tags.push('听辨替换');
  if (/extra:/i.test(input.diffSummary)) tags.push('额外插入');
  if (!tags.length) tags.push('整体复述');

  const suggestions = [
    '再次集中听前 3 秒，关注弱读连读',
    '放慢语速重播并对照原文核对重音位置',
    '把错词抄写三遍，下一轮改挖空练习',
  ];

  return { errorTags: tags, suggestions, provider: 'mock-listening-offline', latencyMs: 12 };
};

const parseStructured = (text: string | null): Record<string, any> | null => {
  if (!text) return null;
  // strip code fences/backticks
  const cleaned = text.replace(/```[a-zA-Z]*\n?/g, '').replace(/```/g, '').trim();
  const match = cleaned.match(/\{[\s\S]*\}/);
  const jsonCandidate = match ? match[0] : cleaned;
  try {
    const parsed = JSON.parse(jsonCandidate);
    return parsed;
  } catch (err) {
    return null;
  }
};

export async function explainListeningWithLLM(input: ListeningLLMInput): Promise<ListeningLLMFeedback> {
  const timeouts = readLLMTimeouts();
  const logger = getLLMLogger();

  // OpenRouter 优先，本地 mock 降级
  const orchestrator = new LLMOrchestrator<ListeningLLMFeedback>(
    [
      buildInvoker('openrouter', async () => {
        const text = await callOpenRouter(
          `你是英语听力辅导老师。根据原句和学生的听写结果返回 JSON。
格式: {"errorTags":["标签1",...],"suggestions":["建议1",...] }
标签不超过3个，建议不超过3条。
原句: ${input.originalSentence}
学生答案: ${input.userAnswer}
差异摘要: ${input.diffSummary}`,
          { jsonMode: true }
        );
        const parsed = parseStructured(text);
        if (parsed && Array.isArray(parsed.errorTags) && Array.isArray(parsed.suggestions)) {
          return {
            errorTags: parsed.errorTags,
            suggestions: parsed.suggestions,
            provider: 'openrouter',
          };
        }
        return null;
      }),
      buildInvoker('mock-listening-offline', async () => craftFallbackFeedback(input)),
    ],
    { totalTimeoutMs: timeouts.totalTimeoutMs, logger }
  );

  const result = await orchestrator.run();
  return result.data;
}

// 静默分档函数
export async function scoreListeningWithLLM(input: ListeningScoreInput): Promise<LlmScore> {
  const timeouts = readLLMTimeouts();
  const logger = getLLMLogger();

  // OpenRouter 优先，本地降级
  const orchestrator = new LLMOrchestrator<LlmScore>(
    [
      buildInvoker('openrouter', async () => {
        const prompt = `作为英语听力评分专家，根据以下信息给出1-9分的分数，返回 JSON。
模式: ${input.mode === 'sentence' ? '整句听写' : '挖空填空'}
原文: ${input.originalSentence}
学生答案: ${input.userAnswer}
正确率: ${input.accuracy}%

返回格式: {"band":数字(1-9),"tags":["标签1","标签2"]}`;
        
        const text = await callOpenRouter(prompt, { jsonMode: true });
        const parsed = parseStructured(text);
        if (parsed && typeof parsed.band === 'number' && parsed.band >= 1 && parsed.band <= 9) {
          return {
            band: parsed.band,
            tags: Array.isArray(parsed.tags) ? parsed.tags : undefined,
            provider: 'openrouter',
            status: 'ready' as const,
          };
        }
        return null;
      }),
      buildInvoker('fallback-score', async () => {
        let band = 1;
        if (input.accuracy >= 95) band = 9;
        else if (input.accuracy >= 85) band = 7;
        else if (input.accuracy >= 70) band = 5;
        else if (input.accuracy >= 50) band = 3;
        
        return {
          band,
          tags: ['本地估算'],
          provider: 'fallback-local',
          status: 'ready' as const,
        };
      }),
    ],
    { totalTimeoutMs: timeouts.totalTimeoutMs, logger }
  );

  try {
    const result = await orchestrator.run();
    return {
      ...result.data,
      status: 'ready',
    };
  } catch (err) {
    logger?.('Silent LLM scoring failed', err);
    return {
      band: 1,
      status: 'failed',
    };
  }
}
